import type { AlchemyType } from '@/domain/genetics/types';

export type AbilityEffect =
  | { kind: 'cone'; radius: number; halfAngle: number; burnMs: number }
  | { kind: 'wave'; width: number; range: number; slowMs: number }
  | { kind: 'slam'; radius: number; shieldMs: number }
  | { kind: 'burst'; radius: number; dashDistance: number }
  | { kind: 'pulse'; radius: number; healRatio: number }
  | { kind: 'dash'; distance: number; lifestealRatio: number };

export interface AbilityDefinition {
  type: AlchemyType;
  cooldownMs: number;
  damageMultiplier: number;
  effect: AbilityEffect;
}

export type AbilityDamageZone =
  | { kind: 'cone'; x: number; y: number; dirX: number; dirY: number; radius: number; halfAngle: number }
  | { kind: 'wave'; x: number; y: number; dirX: number; dirY: number; width: number; range: number }
  | { kind: 'circle'; x: number; y: number; radius: number }
  | { kind: 'dash'; x: number; y: number; dirX: number; dirY: number; distance: number; width: number };

export interface AbilityTarget {
  id: number;
  x: number;
  y: number;
}

export interface AbilityStatusApplication {
  targetId: number;
  kind: 'burn' | 'slow';
  durationMs: number;
}

export interface AbilityOutcome {
  targetIds: number[];
  damageZone: AbilityDamageZone;
  movement: { x: number; y: number } | null;
  healRatio: number;
  shieldMs: number;
  statuses: AbilityStatusApplication[];
}

export interface ResolveAbilityInput {
  ability: AbilityDefinition;
  origin: { x: number; y: number };
  direction: { x: number; y: number };
  targets: readonly AbilityTarget[];
}

export const ABILITY_CATALOG: Readonly<Record<AlchemyType, AbilityDefinition>> = Object.freeze({
  Fire: Object.freeze({
    type: 'Fire', cooldownMs: 5_000, damageMultiplier: 1.25,
    effect: Object.freeze({ kind: 'cone', radius: 96, halfAngle: 35, burnMs: 4_000 }),
  }),
  Water: Object.freeze({
    type: 'Water', cooldownMs: 5_500, damageMultiplier: 1,
    effect: Object.freeze({ kind: 'wave', width: 72, range: 140, slowMs: 2_500 }),
  }),
  Earth: Object.freeze({
    type: 'Earth', cooldownMs: 6_500, damageMultiplier: 1.4,
    effect: Object.freeze({ kind: 'slam', radius: 72, shieldMs: 2_000 }),
  }),
  Air: Object.freeze({
    type: 'Air', cooldownMs: 4_500, damageMultiplier: 0.9,
    effect: Object.freeze({ kind: 'burst', radius: 60, dashDistance: 84 }),
  }),
  Life: Object.freeze({
    type: 'Life', cooldownMs: 6_000, damageMultiplier: 1,
    effect: Object.freeze({ kind: 'pulse', radius: 76, healRatio: 0.15 }),
  }),
  Shadow: Object.freeze({
    type: 'Shadow', cooldownMs: 5_500, damageMultiplier: 1.1,
    effect: Object.freeze({ kind: 'dash', distance: 100, lifestealRatio: 0.25 }),
  }),
});

export function abilityForType(type: AlchemyType): AbilityDefinition {
  return ABILITY_CATALOG[type];
}

export function resolveAbility(input: ResolveAbilityInput): AbilityOutcome {
  const { ability, origin, targets } = input;
  const direction = normalized(input.direction);
  const effect = ability.effect;
  let targetIds: number[];
  let damageZone: AbilityDamageZone;
  let movement: AbilityOutcome['movement'] = null;
  let healRatio = 0;
  let shieldMs = 0;
  let statuses: AbilityStatusApplication[] = [];

  switch (effect.kind) {
    case 'cone':
      damageZone = { kind: 'cone', ...origin, dirX: direction.x, dirY: direction.y, radius: effect.radius, halfAngle: effect.halfAngle };
      targetIds = targets.filter((target) => inCone(target, origin, direction, effect.radius, effect.halfAngle)).map((target) => target.id);
      statuses = targetIds.map((targetId) => ({ targetId, kind: 'burn', durationMs: effect.burnMs }));
      break;
    case 'wave':
      damageZone = { kind: 'wave', ...origin, dirX: direction.x, dirY: direction.y, width: effect.width, range: effect.range };
      targetIds = targets.filter((target) => inLane(target, origin, direction, effect.range, effect.width)).map((target) => target.id);
      statuses = targetIds.map((targetId) => ({ targetId, kind: 'slow', durationMs: effect.slowMs }));
      break;
    case 'slam':
      damageZone = { kind: 'circle', ...origin, radius: effect.radius };
      targetIds = targets.filter((target) => distance(target, origin) <= effect.radius).map((target) => target.id);
      shieldMs = effect.shieldMs;
      break;
    case 'burst':
      damageZone = { kind: 'circle', ...origin, radius: effect.radius };
      targetIds = targets.filter((target) => distance(target, origin) <= effect.radius).map((target) => target.id);
      movement = { x: direction.x * effect.dashDistance, y: direction.y * effect.dashDistance };
      break;
    case 'pulse':
      damageZone = { kind: 'circle', ...origin, radius: effect.radius };
      targetIds = targets.filter((target) => distance(target, origin) <= effect.radius).map((target) => target.id);
      healRatio = effect.healRatio;
      break;
    case 'dash': {
      const width = 56;
      damageZone = { kind: 'dash', ...origin, dirX: direction.x, dirY: direction.y, distance: effect.distance, width };
      targetIds = targets.filter((target) => inLane(target, origin, direction, effect.distance, width)).map((target) => target.id);
      movement = { x: direction.x * effect.distance, y: direction.y * effect.distance };
      healRatio = effect.lifestealRatio;
      break;
    }
  }

  return { targetIds, damageZone, movement, healRatio, shieldMs, statuses };
}

function normalized(vector: { x: number; y: number }): { x: number; y: number } {
  const magnitude = Math.hypot(vector.x, vector.y);
  return magnitude < 0.01 ? { x: 1, y: 0 } : { x: vector.x / magnitude, y: vector.y / magnitude };
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function inCone(
  target: AbilityTarget,
  origin: { x: number; y: number },
  direction: { x: number; y: number },
  radius: number,
  halfAngle: number,
): boolean {
  const dx = target.x - origin.x;
  const dy = target.y - origin.y;
  const magnitude = Math.hypot(dx, dy);
  if (magnitude > radius || magnitude < 0.01) return magnitude < 0.01;
  const cosine = (dx * direction.x + dy * direction.y) / magnitude;
  return cosine >= Math.cos((halfAngle * Math.PI) / 180);
}

function inLane(
  target: AbilityTarget,
  origin: { x: number; y: number },
  direction: { x: number; y: number },
  range: number,
  width: number,
): boolean {
  const dx = target.x - origin.x;
  const dy = target.y - origin.y;
  const forward = dx * direction.x + dy * direction.y;
  const side = Math.abs(dx * -direction.y + dy * direction.x);
  return forward >= 0 && forward <= range && side <= width / 2;
}
